import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@unified-checkout/design-system';
import { CircleExclamation } from '@unified-checkout/design-system/icons/icons/CircleExclamation';

import type { IUpdatesRequiredModalProps } from '@app/dashboard/types/innerLayout/home/onboarding/modals';
import { MODAL_TYPES } from '@app/dashboard/constants/Home/onboarding';

import RejectionCard from './RejectionCard';

import styles from './updates-required-modal.module.scss';

const UpdatesRequiredModal = ({
  activeModal,
  handleCloseModal,
  handleOpenOnboardingUrl,
  rejections = {},
}: IUpdatesRequiredModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const normalizedRejections = useMemo(() => {
    const results: { title: string; descriptions: string[] }[] = [];

    Object.values(rejections).forEach((rejection) => {
      const rejectionKeys = Object.keys(rejection);

      rejectionKeys.forEach((key) => {
        const descriptions = rejection[key];
        if (!descriptions?.length) return;

        results.push({ title: key, descriptions });
      });
    });

    return results;
  }, [rejections]);

  return (
    <Modal
      open={activeModal === MODAL_TYPES.UPDATES_REQUIRED}
      hasCloseIcon
      closeOnClickOutside
      onCloseHandler={handleCloseModal}
      title={t(
        'innerLayout.homePage.onboardingBanner.updatesRequired.modal.title'
      )}
      supportingText={t(
        'innerLayout.homePage.onboardingBanner.updatesRequired.modal.subtitle'
      )}
      headerIcon={CircleExclamation}
      state="danger"
      btnsProps={[
        {
          color: 'primary',
          text: t(
            'innerLayout.homePage.onboardingBanner.updatesRequired.modal.updateDocs'
          ),
          variant: 'contained',
          onClickHandler: () =>
            handleOpenOnboardingUrl({
              setIsWaiting: setIsLoading,
            }),
          disabled: isLoading,
          loading: isLoading,
        },
        {
          color: 'secondary',
          variant: 'outlined',
          text: t('innerLayout.cancel'),
          onClickHandler: handleCloseModal,
          disabled: isLoading,
        },
      ]}
      position="center"
    >
      <div className={styles['updates-required-modal__content']}>
        {normalizedRejections.map(({ title, descriptions }) => (
          <RejectionCard
            key={title}
            title={title}
            descriptions={descriptions}
          />
        ))}
      </div>
    </Modal>
  );
};

export default UpdatesRequiredModal;
